import { z } from 'zod';
import { queryBoolean } from './query.js';

/**
 * F45 — Admin Analytics & Reporting frozen API contracts.
 *
 * See plans/45_F45_admin_analytics_reporting.md (the WHAT) +
 * C:\Users\Tech\.claude\plans\let-us-plan-how-precious-balloon.md (the
 * execution meta-plan + the A1–A10 corrections). Wave 2 sub-agents (2A read /
 * 2B worker+alerts / 2C client / 2D admin web) develop against these contracts
 * in parallel — DO NOT rename, drop, or add fields without escalating to the
 * orchestrator first (contract-amendment protocol; the F23/§38 drift lesson).
 *
 * F45 is READ-ONLY product/usage analytics. It writes NO product data and
 * exposes only AGGREGATES — never an individual-user identifier (D15). Substrate
 * (D1): the Firebase Analytics (GA4) Data API for usage telemetry + existing
 * Postgres tables for transactional metrics, both materialized into the
 * analytics_* rollup tables by the AnalyticsWorker. NO export at MVP (D13).
 * Admin alerts are EMAIL-ONLY (Amendment A1 — admins have no in-app inbox).
 */

// ─── Event names (emitted to Firebase Analytics; D2/D7) ──────────────────────

/**
 * The behavioral/view events F45 instruments to Firebase Analytics. DAU/WAU/MAU
 * come from GA4's automatic active-user metrics (no custom event needed).
 * `venue_cta_click` stays owned by `@bravemove/utils/analytics/venueCtaEvent.ts`
 * (F15) — referenced, NOT redefined here.
 */
export const ANALYTICS_EVENT_NAMES = [
  'app_open',
  'search_performed',
  'venue_viewed',
  'trainer_viewed',
  'tournament_viewed',
  'marketplace_viewed',
  'open_game_viewed',
  'booking_started',
  'trainer_booking_started',
  'registration_started',
] as const;
export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number];

/**
 * Non-PII event params F45's tracker is allowed to forward to GA4 (D15). The
 * tracker STRIPS any param not on this list — defence-in-depth so a raw
 * user_id/name/email/phone can never reach the analytics layer.
 */
export const ANALYTICS_PARAM_ALLOWLIST = [
  'sport',
  'city',
  'governorate',
  'venue_id',
  'trainer_id',
  'tournament_id',
  'country_code',
] as const;
export type AnalyticsParamKey = (typeof ANALYTICS_PARAM_ALLOWLIST)[number];

// ─── KPI / funnel / entity / alert / period enums ────────────────────────────

/** The 12 dashboard-home KPIs (D5). */
export const ANALYTICS_KPI_KEYS = [
  'active_users',
  'new_signups',
  'activation_rate',
  'bookings',
  'booking_funnel',
  'open_games',
  'tournament_registrations',
  'trainer_sessions',
  'subscription_churn',
  'b2c_churn',
  'notification_engagement',
  'geographic_distribution',
] as const;
export type AnalyticsKpiKey = (typeof ANALYTICS_KPI_KEYS)[number];

/** The 4 conversion funnels (D6). */
export const ANALYTICS_FUNNELS = ['booking', 'open_game', 'tournament', 'trainer_booking'] as const;
export type AnalyticsFunnelKey = (typeof ANALYTICS_FUNNELS)[number];

/** `analytics_entity_daily.entity_kind` — top-N + scorecards + geographic (D9/D10/D11). */
export const ANALYTICS_ENTITY_KINDS = [
  'venue',
  'sport',
  'trainer',
  'city',
  'open_game_poster',
] as const;
export type AnalyticsEntityKind = (typeof ANALYTICS_ENTITY_KINDS)[number];

/** The 3 admin alerts (D14). EMAIL-ONLY via AdminEmailService (A1). */
export const ANALYTICS_ALERT_TYPES = [
  'weekly_product_digest',
  'dau_drop_alert',
  'funnel_step_drop_alert',
] as const;
export type AnalyticsAlertType = (typeof ANALYTICS_ALERT_TYPES)[number];

/** Quick-select time windows (D8). Default 30d. */
export const ANALYTICS_PERIODS = ['today', '7d', '30d', '90d', 'ytd', 'custom'] as const;
export type AnalyticsPeriod = (typeof ANALYTICS_PERIODS)[number];

/** F26 channels the notification-engagement panel breaks down by (D12). */
export const ANALYTICS_NOTIFICATION_CHANNELS = ['push', 'whatsapp', 'email', 'in_app'] as const;
export type AnalyticsNotificationChannel = (typeof ANALYTICS_NOTIFICATION_CHANNELS)[number];

// ─── metric_key registries (analytics_metric_daily; the §2.1 contract) ───────

/**
 * Every `analytics_metric_daily.metric_key` F45 writes. The read layer SUMs
 * these over the range EXCEPT the POINT_IN_TIME subset below (read latest-in-
 * range, never SUM — they are GA4 active-user snapshots, not counts).
 */
export const ANALYTICS_METRIC_KEYS = [
  'dau',
  'wau',
  'mau',
  'signups',
  'activation_eligible',
  'activation_converted',
  'bookings',
  'open_games_posted',
  'open_games_joined',
  'tournament_registrations',
  'trainer_sessions',
  'subscription_active_start',
  'subscription_cancelled',
  'b2c_active',
  'b2c_returned',
] as const;
export type AnalyticsMetricKey = (typeof ANALYTICS_METRIC_KEYS)[number];

/** Read latest-in-range, NEVER SUM (GA4 active-user snapshots). */
export const POINT_IN_TIME_METRICS = ['dau', 'wau', 'mau'] as const;
export type PointInTimeMetric = (typeof POINT_IN_TIME_METRICS)[number];

/** Every `analytics_entity_daily.metric_key` (top-N + scorecard rollups). */
export const ANALYTICS_ENTITY_METRIC_KEYS = [
  'bookings',
  'trainer_sessions',
  'open_games',
  'tournaments_hosted',
  'unique_customers',
  'repeat_customers',
  'venue_views',
] as const;
export type AnalyticsEntityMetricKey = (typeof ANALYTICS_ENTITY_METRIC_KEYS)[number];

// ─── Funnel-step registry (the GA4↔Postgres stitch contract; D6) ─────────────

/** Where each funnel step's count comes from (the documented stitch — A: early
 * behavioral steps are GA4 event counts; conversion steps are Postgres rows). */
export type AnalyticsFunnelStep = {
  readonly stepKey: string;
  readonly ordinal: number;
  readonly source: 'ga4' | 'postgres';
};

/**
 * The frozen step list for every funnel — shared by the worker (writes counts),
 * the read service (computes conversions), and the admin fixture/UI (renders).
 * Late steps are Postgres (A9: tournament confirmed=status, paid=payment_status;
 * booking confirmed=status, paid=payment_status; trainer confirmed/paid on
 * trainer bookings; open-game join_requested/join_accepted from F32).
 */
export const ANALYTICS_FUNNEL_STEPS: Readonly<
  Record<AnalyticsFunnelKey, readonly AnalyticsFunnelStep[]>
> = {
  booking: [
    { stepKey: 'search_performed', ordinal: 1, source: 'ga4' },
    { stepKey: 'venue_viewed', ordinal: 2, source: 'ga4' },
    { stepKey: 'booking_started', ordinal: 3, source: 'ga4' },
    { stepKey: 'booking_confirmed', ordinal: 4, source: 'postgres' },
    { stepKey: 'booking_paid', ordinal: 5, source: 'postgres' },
  ],
  open_game: [
    { stepKey: 'marketplace_viewed', ordinal: 1, source: 'ga4' },
    { stepKey: 'open_game_viewed', ordinal: 2, source: 'ga4' },
    { stepKey: 'join_requested', ordinal: 3, source: 'postgres' },
    { stepKey: 'join_accepted', ordinal: 4, source: 'postgres' },
    { stepKey: 'booking_paid', ordinal: 5, source: 'postgres' },
  ],
  tournament: [
    { stepKey: 'tournament_viewed', ordinal: 1, source: 'ga4' },
    { stepKey: 'registration_started', ordinal: 2, source: 'ga4' },
    { stepKey: 'registration_confirmed', ordinal: 3, source: 'postgres' },
    { stepKey: 'registration_paid', ordinal: 4, source: 'postgres' },
  ],
  trainer_booking: [
    { stepKey: 'trainer_marketplace_viewed', ordinal: 1, source: 'ga4' },
    { stepKey: 'trainer_viewed', ordinal: 2, source: 'ga4' },
    { stepKey: 'trainer_booking_started', ordinal: 3, source: 'ga4' },
    { stepKey: 'trainer_booking_confirmed', ordinal: 4, source: 'postgres' },
    { stepKey: 'trainer_booking_paid', ordinal: 5, source: 'postgres' },
  ],
} as const;

// ─── Read shapes ─────────────────────────────────────────────────────────────

/**
 * One KPI card. `value` is the current-period value. With compare-to-previous
 * on, `previous` + `deltaPercent` are populated. `unit` distinguishes counts
 * from rates (activation/churn are percent). `booking_funnel`/`notification_
 * engagement`/`geographic_distribution` are summary cards whose detail lives in
 * the dedicated panels — their `value` is the headline number (paid bookings /
 * overall open-rate / total cities).
 */
export type AnalyticsKpiCard = {
  readonly key: AnalyticsKpiKey;
  readonly value: number;
  readonly unit: 'count' | 'percent';
  readonly previous?: number;
  readonly deltaPercent?: number;
};

export type AnalyticsActiveUsersPoint = {
  /** ISO 8601 date (Asia/Beirut day). */
  readonly day: string;
  readonly dau: number;
  readonly wau: number;
  readonly mau: number;
};

export type AnalyticsDayCount = { readonly day: string; readonly count: number };

export type AnalyticsSportMixRow = { readonly sport: string; readonly count: number };

export type AnalyticsCityRow = {
  readonly cityCode: string;
  readonly cityName: string;
  readonly count: number;
};

/** Activity-by-hour heatmap cell (dow 0=Sun..6=Sat, hour 0..23, Asia/Beirut). */
export type AnalyticsHeatmapCell = {
  readonly dow: number;
  readonly hour: number;
  readonly count: number;
};

export type AnalyticsTopRow = {
  readonly entityKind: AnalyticsEntityKind;
  readonly entityId: string;
  readonly entityName: string;
  readonly value: number;
  /** Deep-link into the owning admin surface (F42 venue / F43 user / internal). null = no target (sport). */
  readonly deepLinkPath: string | null;
};

/** One funnel's step results (`GET /admin/analytics/funnel`). */
export type AnalyticsFunnelStepResult = {
  readonly stepKey: string;
  readonly ordinal: number;
  readonly source: 'ga4' | 'postgres';
  readonly count: number;
  /** count[n] / count[n-1] (undefined for the first step). */
  readonly conversionFromPrev?: number;
  /** 1 − conversionFromPrev. */
  readonly dropoffFromPrev?: number;
  readonly previousCount?: number;
  readonly deltaPercent?: number;
};

export type AnalyticsFunnelResult = {
  readonly funnel: AnalyticsFunnelKey;
  readonly period: { readonly from: string; readonly to: string };
  readonly compareTo?: { readonly from: string; readonly to: string };
  readonly segment?: { readonly dim: string; readonly val: string };
  readonly steps: readonly AnalyticsFunnelStepResult[];
};

/** D1/D7/D30 retention cohorts by signup-week (`GET /admin/analytics/retention`). */
export type AnalyticsRetentionCohort = {
  /** ISO date of the cohort week start. */
  readonly cohortWeek: string;
  readonly size: number;
  /** Retained fraction at day-1 / day-7 / day-30 (0..1). */
  readonly d1: number;
  readonly d7: number;
  readonly d30: number;
};

export type AnalyticsRetention = {
  readonly cohorts: readonly AnalyticsRetentionCohort[];
};

/** `GET /admin/analytics/dashboard` response. */
export type AnalyticsDashboard = {
  readonly period: { readonly from: string; readonly to: string };
  readonly compareTo?: { readonly from: string; readonly to: string };
  readonly kpis: readonly AnalyticsKpiCard[];
  readonly activeUsersTrend: readonly AnalyticsActiveUsersPoint[];
  readonly signupsByDay: readonly AnalyticsDayCount[];
  readonly bookingFunnelSummary: AnalyticsFunnelResult;
  readonly sportMix: readonly AnalyticsSportMixRow[];
  readonly cityDistribution: readonly AnalyticsCityRow[];
  readonly activityByHour: readonly AnalyticsHeatmapCell[];
  readonly topVenues: readonly AnalyticsTopRow[];
  readonly topSports: readonly AnalyticsTopRow[];
  readonly topTrainers: readonly AnalyticsTopRow[];
  readonly topCities: readonly AnalyticsTopRow[];
  readonly topOpenGamePosters: readonly AnalyticsTopRow[];
  /** analytics_worker_state.last_daily_rollup_at — drives the daily-panel banner. */
  readonly lastDailyRefreshedAt: string | null;
  /** analytics_worker_state.last_today_rollup_at — drives the "Today" banner. */
  readonly lastTodayRefreshedAt: string | null;
};

/** Per-venue / per-sport / per-trainer scorecard (D10). */
export type AnalyticsScorecard = {
  readonly entityKind: 'venue' | 'sport' | 'trainer';
  readonly entityId: string;
  readonly entityName: string;
  readonly period: { readonly from: string; readonly to: string };
  readonly compareTo?: { readonly from: string; readonly to: string };
  readonly kpis: readonly AnalyticsKpiCard[];
  readonly funnel: AnalyticsFunnelResult;
  /** repeat customers / unique customers (0..1). Undefined when no bookings. */
  readonly repeatCustomerRate?: number;
  /** Average rating (post-F37). Undefined until reviews exist. */
  readonly avgRating?: number;
  readonly openGameCount: number;
  readonly tournamentCount: number;
  /** Deep-links into the owning admin surfaces (F42 / F43 / forward-ref F44). */
  readonly deepLinks: Readonly<Record<string, string>>;
};

/** One row of the notification-engagement panel (D12; A7 mapping). */
export type AnalyticsNotificationEngagementRow = {
  readonly category: string;
  readonly channel: AnalyticsNotificationChannel;
  readonly sent: number;
  readonly delivered: number;
  /** Email "opened" is NOT tracked (F28 D10) — null for the email channel. */
  readonly opened: number | null;
  readonly tapped: number | null;
  readonly openRate: number | null;
  readonly tapRate: number | null;
};

export type AnalyticsNotificationEngagement = {
  readonly rows: readonly AnalyticsNotificationEngagementRow[];
};

/** `GET /admin/analytics/geographic` response. */
export type AnalyticsGeographic = {
  readonly period: { readonly from: string; readonly to: string };
  readonly cities: readonly AnalyticsCityRow[];
};

/** One configurable alert rule (`GET /admin/analytics/alert-rules`). */
export type AnalyticsAlertRule = {
  readonly ruleType: AnalyticsAlertType;
  readonly enabled: boolean;
  /** dau_drop = % WoW; funnel_step_drop = pp WoW; weekly_digest = null. */
  readonly thresholdPercent: number | null;
  readonly digestDow: number | null;
  readonly digestHour: number | null;
  readonly lastCheckedAt: string | null;
  readonly lastFiredAt: string | null;
  readonly updatedAt: string;
};

/** `POST /admin/analytics/refresh` response. */
export type AnalyticsRefreshResult = {
  readonly lastDailyRefreshedAt: string;
  readonly lastTodayRefreshedAt: string;
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/** ISO `YYYY-MM-DD` calendar date (Asia/Beirut). */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/** `GET /admin/analytics/dashboard?from&to&compare&segment` */
export const AnalyticsDashboardQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    compare: queryBoolean().optional().default(false),
    segmentDim: z.enum(['all', 'sport', 'city', 'governorate']).optional().default('all'),
    segmentVal: z.string().min(1).max(120).optional(),
  })
  .strict();
export type AnalyticsDashboardQuery = z.infer<typeof AnalyticsDashboardQuerySchema>;

/** `GET /admin/analytics/funnel?funnel&from&to&compare&segmentDim&segmentVal` */
export const AnalyticsFunnelQuerySchema = z
  .object({
    funnel: z.enum(ANALYTICS_FUNNELS),
    from: isoDate,
    to: isoDate,
    compare: queryBoolean().optional().default(false),
    segmentDim: z.enum(['all', 'sport', 'city']).optional().default('all'),
    segmentVal: z.string().min(1).max(120).optional(),
  })
  .strict();
export type AnalyticsFunnelQuery = z.infer<typeof AnalyticsFunnelQuerySchema>;

/** `GET /admin/analytics/retention?from&to&segment` */
export const AnalyticsRetentionQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    segmentDim: z.enum(['all', 'sport', 'city']).optional().default('all'),
    segmentVal: z.string().min(1).max(120).optional(),
  })
  .strict();
export type AnalyticsRetentionQuery = z.infer<typeof AnalyticsRetentionQuerySchema>;

/** `GET /admin/analytics/top?entityKind&metric&from&to` */
export const AnalyticsTopNQuerySchema = z
  .object({
    entityKind: z.enum(ANALYTICS_ENTITY_KINDS),
    metric: z.enum(ANALYTICS_ENTITY_METRIC_KEYS),
    from: isoDate,
    to: isoDate,
    limit: z.coerce.number().int().min(1).max(50).optional().default(10),
  })
  .strict();
export type AnalyticsTopNQuery = z.infer<typeof AnalyticsTopNQuerySchema>;

/** `GET /admin/analytics/scorecards/{venues|sports|trainers}/:id?from&to&compare` */
export const AnalyticsScorecardQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    compare: queryBoolean().optional().default(false),
  })
  .strict();
export type AnalyticsScorecardQuery = z.infer<typeof AnalyticsScorecardQuerySchema>;

/** `GET /admin/analytics/geographic?from&to` */
export const AnalyticsGeographicQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
  })
  .strict();
export type AnalyticsGeographicQuery = z.infer<typeof AnalyticsGeographicQuerySchema>;

/** `GET /admin/analytics/notification-engagement?from&to&category&channel` */
export const AnalyticsNotificationEngagementQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    category: z.string().min(1).max(64).optional(),
    channel: z.enum(ANALYTICS_NOTIFICATION_CHANNELS).optional(),
  })
  .strict();
export type AnalyticsNotificationEngagementQuery = z.infer<
  typeof AnalyticsNotificationEngagementQuerySchema
>;

/** `PATCH /admin/analytics/alert-rules/:ruleType` body (partial update). */
export const UpdateAnalyticsAlertRuleInputSchema = z
  .object({
    enabled: z.boolean().optional(),
    thresholdPercent: z.number().min(0).max(100).nullable().optional(),
    digestDow: z.number().int().min(0).max(6).nullable().optional(),
    digestHour: z.number().int().min(0).max(23).nullable().optional(),
  })
  .strict();
export type UpdateAnalyticsAlertRuleInput = z.infer<typeof UpdateAnalyticsAlertRuleInputSchema>;

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F45 error vocabulary. Service maps to HTTP:
 *   - `ANALYTICS_RANGE_INVALID` / `ANALYTICS_ALERT_RULE_INVALID` /
 *     `ANALYTICS_SORT_REQUIRES_SEGMENT` → 422
 *   - `ANALYTICS_ENTITY_NOT_FOUND` → 404
 *   - `ANALYTICS_REFRESH_RATE_LIMITED` → 429
 *   - `ANALYTICS_GA4_UNAVAILABLE` → 503 (read path never hits GA4 live; this is
 *     surfaced only by the manual-refresh path when the worker can't reach GA4)
 */
export const ANALYTICS_ERROR_CODES = [
  'ANALYTICS_RANGE_INVALID',
  'ANALYTICS_SORT_REQUIRES_SEGMENT',
  'ANALYTICS_REFRESH_RATE_LIMITED',
  'ANALYTICS_ALERT_RULE_INVALID',
  'ANALYTICS_GA4_UNAVAILABLE',
  'ANALYTICS_ENTITY_NOT_FOUND',
] as const;
export type AnalyticsErrorCode = (typeof ANALYTICS_ERROR_CODES)[number];
